// Cross-file content search. A single field (lucide Search inside), debounced,
// with results grouped per file: a file header row, then each matching line with
// its 1-based line number and the match highlighted. Clicking any row opens the
// file. Quiet empty + no-results states keep it from reading like a wireframe.

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import { Search as SearchIcon, X } from "lucide-react";
import { useIde, type VFile } from "../store";
import { FileGlyph, glyphClass, kindOf, type FileKind } from "./icons";
import { splitExt } from "./tree";
import { highlight, type HlSeg } from "../editor/sealHighlight";

interface SearchHit {
  line: number; // 1-based
  text: string; // full line text
  start: number; // match start column within `text`
  end: number; // match end column within `text`
}

interface SearchFileResult {
  path: string;
  kind: FileKind;
  hits: SearchHit[];
}

const PER_FILE_CAP = 200;

function searchFiles(files: Record<string, VFile>, query: string): SearchFileResult[] {
  const needle = query.toLowerCase();
  if (!needle) return [];
  const results: SearchFileResult[] = [];

  for (const file of Object.values(files)) {
    const lines = file.content.split("\n");
    const hits: SearchHit[] = [];
    for (let i = 0; i < lines.length; i++) {
      const text = lines[i] ?? "";
      const idx = text.toLowerCase().indexOf(needle);
      if (idx >= 0) {
        hits.push({ line: i + 1, text, start: idx, end: idx + needle.length });
        if (hits.length >= PER_FILE_CAP) break;
      }
    }
    if (hits.length > 0) results.push({ path: file.path, kind: kindOf(file), hits });
  }

  results.sort((a, b) => a.path.localeCompare(b.path));
  return results;
}

/** Plain segments (no syntax classes), match-marked, for non-Seal files. */
function plainSegs(line: string, mStart: number, mEnd: number): HlSeg[] {
  const segs: HlSeg[] = [];
  if (mStart > 0) segs.push({ value: line.slice(0, mStart), cls: "", matched: false });
  if (mEnd > mStart) segs.push({ value: line.slice(mStart, mEnd), cls: "", matched: true });
  if (mEnd < line.length) segs.push({ value: line.slice(mEnd), cls: "", matched: false });
  return segs;
}

/** A matched line, trimmed to keep the match visible, Seal-syntax-highlighted
 *  (for .sl files) with the matched substring background-marked. */
function HitText({ hit, syntax }: { hit: SearchHit; syntax: boolean }): JSX.Element {
  const { text, start, end } = hit;
  const lead = Math.max(0, start - 24);
  const visible = text.slice(lead);
  const ms = start - lead;
  const me = end - lead;
  const segs = syntax ? highlight(visible, ms, me) : plainSegs(visible, ms, me);
  return (
    <span className="exp-hit-text">
      {lead > 0 && <span className="exp-hit-elide">…</span>}
      {segs.map((s, i) => {
        const cls = `${s.cls ? `bsh-${s.cls}` : ""}${s.matched ? " exp-hit-mark" : ""}`.trim();
        return (
          <span key={i} className={cls || undefined}>
            {s.value}
          </span>
        );
      })}
    </span>
  );
}

export function Search(): JSX.Element {
  const files = useIde((s) => s.files);
  const openFile = useIde((s) => s.openFile);
  const activePath = useIde((s) => s.activePath);

  const [raw, setRaw] = useState("");
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the field when Search opens.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Debounce (160ms) so typing doesn't re-scan on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setQuery(raw), 160);
    return () => clearTimeout(t);
  }, [raw]);

  const results = useMemo(() => searchFiles(files, query.trim()), [files, query]);
  const totalHits = useMemo(() => results.reduce((n, r) => n + r.hits.length, 0), [results]);
  const trimmed = query.trim();
  const capped = useMemo(() => results.some((r) => r.hits.length >= PER_FILE_CAP), [results]);

  const clear = (): void => {
    setRaw("");
    setQuery("");
    inputRef.current?.focus();
  };

  return (
    <div className="exp">
      <header className="exp-header">
        <span className="label-caps exp-header-title">Search</span>
      </header>

      <div className="exp-search-field">
        <span className="exp-search-icon" aria-hidden="true">
          <SearchIcon size={14} strokeWidth={1.75} />
        </span>
        <input
          ref={inputRef}
          className="exp-search-input"
          type="text"
          value={raw}
          placeholder="Search files"
          spellCheck={false}
          autoComplete="off"
          aria-label="Search file contents"
          onChange={(e) => setRaw(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape" && raw) {
              e.preventDefault();
              clear();
            }
          }}
        />
        {raw && (
          <button
            type="button"
            className="exp-search-clear"
            title="Clear (Esc)"
            aria-label="Clear search"
            onClick={clear}
          >
            <X size={13} strokeWidth={2} />
          </button>
        )}
      </div>

      {trimmed && (
        <div className="exp-search-summary" aria-live="polite">
          {totalHits === 0 ? (
            <>
              No results for <span className="exp-search-needle">{trimmed}</span>
            </>
          ) : (
            <>
              <strong>{totalHits}</strong> {totalHits === 1 ? "result" : "results"} in{" "}
              <strong>{results.length}</strong> {results.length === 1 ? "file" : "files"}
              {capped && <span className="exp-search-capped"> · capped per file</span>}
            </>
          )}
        </div>
      )}

      <div className="exp-body exp-search-results" role="list">
        {!trimmed && (
          <div className="exp-empty">
            <span className="exp-empty-icon" aria-hidden="true">
              <SearchIcon size={22} strokeWidth={1.5} />
            </span>
            <div className="exp-empty-title">Search across files</div>
            <p className="exp-empty-text">
              Find matches in every contract and args file. Results group by file.
            </p>
          </div>
        )}

        {trimmed &&
          results.map((r) => {
            const { base, ext } = splitExt(r.path);
            return (
              <div key={r.path} role="listitem" className="exp-search-group">
                <button
                  type="button"
                  className={`exp-search-file${activePath === r.path ? " is-active" : ""}`}
                  title={r.path}
                  onClick={() => openFile(r.path)}
                >
                  <span className={glyphClass(r.kind)} aria-hidden="true">
                    <FileGlyph kind={r.kind} />
                  </span>
                  <span className="exp-search-file-name">
                    {base}
                    {ext && <span className="exp-row-ext">{ext}</span>}
                  </span>
                  <span className="exp-search-file-count">{r.hits.length}</span>
                </button>
                {r.hits.map((hit) => (
                  <button
                    key={`${r.path}:${hit.line}:${hit.start}`}
                    type="button"
                    className="exp-search-hit"
                    title={`${r.path}:${hit.line}`}
                    onClick={() => openFile(r.path)}
                  >
                    <span className="exp-search-hit-line">{hit.line}</span>
                    <HitText hit={hit} syntax={r.kind === "seal"} />
                  </button>
                ))}
              </div>
            );
          })}
      </div>
    </div>
  );
}
